import React, { useState } from 'react';
import { CreditCard, Loader2, CheckCircle, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { adminAPI, ordersAPI } from '../services/api';

// Admin order drawer: refund a SumUp-paid order on the customer's card and issue the credit note (avoir)
const SumupRefundPanel = ({ order, onDone }) => {
  const cardPaid = Number(order.sumup_amount ?? order.paid_amount ?? 0);
  const [amount, setAmount] = useState(cardPaid.toFixed(2));
  const [reason, setReason] = useState('');
  const [restock, setRestock] = useState(true);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  if (order.refund) {
    const r = order.refund;
    return (
      <div className="text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg px-3 py-2 flex flex-wrap items-center gap-2" data-testid="order-sumup-refunded">
        <CheckCircle size={16} /> Remboursé sur la carte via SumUp : <strong>{Number(r.amount).toFixed(2)} €</strong>{r.full ? '' : ' (partiel)'} le {new Date(r.at).toLocaleDateString('fr-FR')} ({r.author}) · avoir <strong>{r.credit_note_number}</strong>
        <button type="button" onClick={() => ordersAPI.downloadCreditNote(order)} className="ml-auto inline-flex items-center gap-1 text-[#1e3a5f] font-medium hover:underline" data-testid="order-credit-note-download"><FileText size={14} /> Avoir PDF</button>
      </div>
    );
  }
  if (order.payment_status !== 'paid' || !order.sumup_transaction_id || order.wallet_refund) return null;

  const refund = async () => {
    const v = parseFloat(String(amount).replace(',', '.'));
    if (!v || v <= 0 || v > cardPaid + 0.001) { toast.error(`Montant invalide (maximum ${cardPaid.toFixed(2)} €)`); return; }
    const full = Math.abs(v - cardPaid) < 0.005;
    if (!window.confirm(`Rembourser ${v.toFixed(2)} € sur la carte bancaire du client via SumUp ?${full ? ' La commande passera en « Remboursée » et l\'avoir sera émis.' : ' Un avoir partiel sera émis.'} Cette opération est irréversible.`)) return;
    setBusy(true);
    try {
      const r = await adminAPI.sumupRefund(order.id, { amount: v, reason, restock });
      toast.success(`${r.message}${r.restocked_qty ? ` · stock restitué (+${r.restocked_qty})` : ''}${r.wallet_back ? ` · avoir ${r.wallet_back.toFixed(2)} € recrédité` : ''}`);
      onDone && onDone(r);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Remboursement SumUp impossible');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-dashed border-emerald-600/50 rounded-lg p-3 bg-emerald-50/40" data-testid="order-sumup-refund-panel">
      {!open ? (
        <button type="button" onClick={() => setOpen(true)} data-testid="order-sumup-refund-open" className="text-sm font-medium text-emerald-800 inline-flex items-center gap-2 hover:underline"><CreditCard size={16} /> Rembourser sur la carte via SumUp (+ avoir client)</button>
      ) : (
        <div className="space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Montant (€) — payé par carte : {cardPaid.toFixed(2)} €</label>
              <Input data-testid="order-sumup-refund-amount" type="number" min="0.01" step="0.01" max={cardPaid} value={amount} onChange={(e) => setAmount(e.target.value)} className="w-36" />
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs font-medium text-gray-600 mb-1">Motif (facultatif, imprimé sur l'avoir)</label>
              <Input data-testid="order-sumup-refund-reason" maxLength={200} placeholder="Ex. : article retourné, rupture fournisseur…" value={reason} onChange={(e) => setReason(e.target.value)} />
            </div>
          </div>
          <label className="flex items-center gap-2 text-xs text-gray-600"><input type="checkbox" data-testid="order-sumup-refund-restock" checked={restock} onChange={(e) => setRestock(e.target.checked)} /> Restituer le stock (remboursement total uniquement)</label>
          <p className="text-xs text-gray-500">Le remboursement est exécuté immédiatement chez SumUp sur la carte d'origine (3 à 10 jours ouvrés côté banque). Un avoir « AV-… » est généré, le client reçoit un e-mail avec le lien de téléchargement.{Number(order.wallet_used || 0) > 0 ? ` L'avoir de ${Number(order.wallet_used).toFixed(2)} € utilisé à la commande sera recrédité sur le compte de dépôt en cas de remboursement total.` : ''}</p>
          <div className="flex gap-2">
            <Button onClick={refund} disabled={busy} data-testid="order-sumup-refund-confirm" className="bg-emerald-700 hover:bg-emerald-800">{busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <CreditCard size={16} className="mr-2" />} Rembourser via SumUp</Button>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="order-sumup-refund-cancel">Annuler</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SumupRefundPanel;
